import React from 'react';
import clsx from 'clsx';
import capitalize from 'lodash/capitalize';
import {useCallback} from 'react';

import {react2panel} from 'common/util/react2panel';
import {withStyles} from 'common/util/react';

import {SvgIconReact} from '../svg-icon';
import {SvgIconType} from '../svg-icon/types';
import styles from './index.module.styl';
import css from './index.module.styl?inline';
import {CheckboxProps, CheckboxTheme} from './types';

function Checkbox({
  checked,
  focused,
  disabled,
  theme = CheckboxTheme.Blue,
  icon,
  checkedColor,
  checkedIcon = `checkmark`,
  uncheckedIcon,
  children,
  className,
  onClick,
  onPointerEnter,
  onPointerLeave,
  elref,
  id,
  'data-testid': dataTestId,
}: CheckboxProps & React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>) {
  const getIconAttributes = useCallback(() => {
    const color = checked && checkedColor !== `none` ? `white` : undefined;

    return {
      icon: (checked ? checkedIcon : uncheckedIcon) as SvgIconType,
      color,
      empty: !checked && !uncheckedIcon,
    };
  }, [checked, checkedColor, uncheckedIcon, checkedIcon]);
  const getIconStyles = useCallback(() => {
    if (!checkedColor) {
      return {};
    }

    let border = `none`;

    if ((!checked && !focused) || (checked && checkedColor === `none`)) {
      border = `1px solid #CFD6E4`; // $gray2
    } else {
      border = `1px solid ${checkedColor}`;
    }

    return {
      border,
      ...(checked ? {backgroundColor: checkedColor} : {}),
    };
  }, [checkedColor, checked, focused]);

  const getClasses = useCallback(() => {
    return {
      [styles.mpCheckboxChecked]: !!checked,
      [styles.mpCheckboxDisabled]: !!disabled,
      [styles.mpCheckboxFocused]: !!focused,
      // [styles.mpCheckboxBlue]: !this.attr(`checked-color`),
      [styles[`mpCheckbox` + capitalize(theme)]]: !checkedColor, // if color is provided, override all theming
    };
  }, [checked, disabled, focused, checkedColor, theme]);
  const iconStyles = getIconStyles();
  return (
    <div
      className={clsx(`mp-checkbox-wrapper`, className)}
      onClick={onClick}
      role={`checkbox`}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      elref={elref}
      id={id}
      aria-checked={checked}
      aria-disabled={disabled}
      data-testid={dataTestId}
    >
      <div className={clsx(styles.mpCheckboxWrapper, getClasses())}>
        <div
          className={styles.mpCheckboxIcon}
          // HACK: for unknown reasons, when the component is re-rendered with new iconStyles, it's not updating the
          // inline styles here. Adding a key to force the element to be recreated fixes the issue.
          key={JSON.stringify(iconStyles)}
          style={iconStyles}
        >
          <div className={styles.mpCheckboxAnimationLayer} />
          <SvgIconReact {...getIconAttributes()} />
        </div>
        {icon ? (
          <div className={styles.mpCheckboxAdditionalIcon}>
            <SvgIconReact icon={icon as SvgIconType} />
          </div>
        ) : null}
        <div className={styles.mpCheckboxLabel}>{children}</div>
      </div>
    </div>
  );
}

export const CheckboxEl = react2panel(`mp-checkbox`, withStyles(Checkbox, css));
export const CheckboxReact = Checkbox;
